import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

import { API_URL, api, getPosHeaders } from "./api";
import { usePermissions } from "./PermissionContext";
import SaleReturnModal from "./components/SaleReturnModal";

import {
  openSaleReceiptPrintWindow,
  printSaleReceipt,
} from "./printing/SaleReceiptPrinter";

import "./CompanySales.css";
import "./UltimosMovimientosPOS.css";

const SALES_LIMIT = 8;

const currencyFormatter = new Intl.NumberFormat("es-CO", {
  style: "currency",
  currency: "COP",
  maximumFractionDigits: 2,
});


function getErrorMessage(error) {
  const detail = error.response?.data?.detail;

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    return detail
      .map((item) => item.msg)
      .filter(Boolean)
      .join(". ");
  }

  return error.message || "No fue posible completar la operación.";
}


function getResponseItems(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data)) return data.data;

  throw new Error(
    "El servidor devolvió un listado de ventas inválido."
  );
}


function money(value) {
  const amount = Number(value ?? 0);

  return currencyFormatter.format(
    Number.isFinite(amount) ? amount : 0
  );
}


function formatDate(value) {
  if (!value) return "-";

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? "-"
    : date.toLocaleString("es-CO");
}


function formatEntryDate(value) {
  if (!value) return "-";

  const date = new Date(
    /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? `${value}T00:00:00`
      : value
  );

  return Number.isNaN(date.getTime())
    ? "-"
    : date.toLocaleDateString("es-CO");
}


function getPaymentLabel(method) {
  const labels = {
    cash: "Efectivo",
    card: "Tarjeta",
    transfer: "Transferencia",
    credit: "Crédito",
    mixed: "Mixto",
  };

  return labels[method] || method || "-";
}


function getSaleStatusLabel(status) {
  const labels = {
    completed: "Completada",
    partially_refunded: "Reintegro parcial",
    cancelled: "Anulada",
  };

  return labels[status] || status || "-";
}


function normalizeText(value) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}


function validColor(value, fallback) {
  if (
    typeof value === "string" &&
    value.trim() &&
    !value.includes("var(") &&
    typeof CSS !== "undefined" &&
    CSS.supports("color", value.trim())
  ) {
    return value.trim();
  }

  return fallback;
}


export default function UltimosMovimientosPOS() {
  const { company } = useParams();
  const { can } = usePermissions();

  const canViewSales = can("sales.view");
  const canCancelSales = can("sales.cancel");
  const canViewAccounting = can("accounting.view");

  const [recentSales, setRecentSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [saleModal, setSaleModal] = useState(null);

  const [
    linkedJournalEntry,
    setLinkedJournalEntry,
  ] = useState(null);

  const [openingAction, setOpeningAction] = useState(false);
  const [branding, setBranding] = useState(null);

  const [
    reprintingSaleId,
    setReprintingSaleId,
  ] = useState(null);

  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(5);
  const [page, setPage] = useState(1);

  const getHeaders = useCallback(
    () => getPosHeaders(company),
    [company]
  );

  // Identidad visual de la empresa.
  useEffect(() => {
    const controller = new AbortController();

    setBranding(null);
    setSaleModal(null);
    setLinkedJournalEntry(null);
    setSearch("");
    setPage(1);

    api
      .get(
        `/companies/by-subdomain/${
          encodeURIComponent(company)
        }/branding`,
        {
          signal: controller.signal,
        }
      )
      .then(({ data }) => {
        if (!controller.signal.aborted) {
          setBranding({
            ...data,
            subdomain: company,
          });
        }
      })
      .catch(() => {
        // Conserva los colores predeterminados
        // si la consulta de marca no está disponible.
      });

    return () => controller.abort();
  }, [company]);

  // Movimientos del usuario autenticado.
  const loadRecentSales = useCallback(
    async (signal) => {
      setLoading(true);
      setError("");

      try {
        const response = await axios.get(
          `${API_URL}/pos/sales/me`,
          {
            headers: getHeaders(),
            params: {
              limit: SALES_LIMIT,
            },
            signal,
          }
        );

        if (!signal?.aborted) {
          setRecentSales(
            getResponseItems(response.data)
          );
        }
      } catch (requestError) {
        if (!signal?.aborted) {
          setError(getErrorMessage(requestError));
        }
      } finally {
        if (!signal?.aborted) {
          setLoading(false);
        }
      }
    },
    [getHeaders]
  );

  useEffect(() => {
    const controller = new AbortController();

    setRecentSales([]);
    setPage(1);

    if (canViewSales) {
      loadRecentSales(controller.signal);
    } else {
      setLoading(false);
    }

    return () => controller.abort();
  }, [canViewSales, loadRecentSales]);

  // Búsqueda sobre los registros cargados.
  const filteredSales = useMemo(() => {
    const query = normalizeText(search.trim());

    if (!query) return recentSales;

    return recentSales.filter((sale) => {
      const searchableText = [
        sale.document_number || `#${sale.id}`,
        sale.customer_name || "Cliente general",
        getPaymentLabel(sale.payment_method),
        getSaleStatusLabel(sale.status),
        formatDate(sale.created_at),
        sale.total,
        money(sale.total),
      ].join(" ");

      return normalizeText(searchableText).includes(query);
    });
  }, [recentSales, search]);

  // Paginación local.
  const totalPages = Math.max(
    1,
    Math.ceil(filteredSales.length / pageSize)
  );

  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * pageSize;

  const visibleSales = filteredSales.slice(
    startIndex,
    startIndex + pageSize
  );

  const firstRecord = filteredSales.length
    ? startIndex + 1
    : 0;

  const lastRecord = Math.min(
    startIndex + pageSize,
    filteredSales.length
  );

  async function handleReprintSale(saleId) {
    if (
      !canViewSales ||
      openingAction ||
      loading ||
      reprintingSaleId !== null
    ) {
      return;
    }

    // Abrir inmediatamente conserva la acción del usuario
    // mientras se consulta el comprobante original.
    const printWindow = openSaleReceiptPrintWindow();

    if (!printWindow) {
      toast.error(
        "Permite las ventanas emergentes para imprimir el comprobante."
      );
      return;
    }

    setOpeningAction(true);
    setReprintingSaleId(saleId);

    try {
      const response = await axios.get(
        `${API_URL}/pos/sales/${saleId}/receipt`,
        {
          headers: getHeaders(),
        }
      );

      if (printWindow.closed) return;

      const prepared = printSaleReceipt(
        response.data,
        printWindow,
        {
          isCopy: true,
        }
      );

      if (!prepared) {
        if (!printWindow.closed) {
          printWindow.close();
        }

        toast.error(
          "No fue posible preparar la copia del comprobante."
        );
      }
    } catch (requestError) {
      if (!printWindow.closed) {
        printWindow.close();
      }

      toast.error(getErrorMessage(requestError));
    } finally {
      setOpeningAction(false);
      setReprintingSaleId(null);
    }
  }

  async function handleViewJournalEntry(sale) {
    if (openingAction || !canViewAccounting) return;

    setOpeningAction(true);

    try {
      const response = await axios.get(
        `${API_URL}/pos/accounting/source-entry/sale/${sale.id}`,
        {
          headers: getHeaders(),
        }
      );

      setLinkedJournalEntry(response.data);
    } catch (requestError) {
      if (requestError.response?.status === 404) {
        toast.info(
          "Esta venta todavía no tiene un comprobante contable asociado."
        );
      } else {
        toast.error(getErrorMessage(requestError));
      }
    } finally {
      setOpeningAction(false);
    }
  }

  async function openSaleModal(saleId, mode) {
    if (openingAction || !canViewSales) return;
    if (mode !== "detail" && !canCancelSales) return;

    setOpeningAction(true);

    try {
      const response = await axios.get(
        `${API_URL}/pos/sales/${saleId}`,
        {
          headers: getHeaders(),
        }
      );

      setSaleModal({
        sale: response.data,
        mode,
      });
    } catch (requestError) {
      toast.error(getErrorMessage(requestError));
    } finally {
      setOpeningAction(false);
    }
  }

  useEffect(() => {
    if (!linkedJournalEntry) return undefined;

    function handleEscape(event) {
      if (event.key === "Escape") {
        setLinkedJournalEntry(null);
      }
    }

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, [linkedJournalEntry]);

  const currentBranding =
    branding?.subdomain === company
      ? branding
      : null;

  const primary = validColor(
    currentBranding?.primary_color,
    "#1f5d7a"
  );

  const secondary = validColor(
    currentBranding?.secondary_color,
    primary
  );

  const theme = {
    "--company-primary": primary,
    "--company-secondary": secondary,
    "--company-primary-dark": secondary,
    "--pos-primary": primary,
    "--pos-primary-dark": secondary,
    "--pos-primary-soft": `color-mix(in srgb, ${primary} 8%, white)`,
    "--pos-brand-border": `color-mix(in srgb, ${primary} 22%, white)`,
    "--pos-brand-focus": `color-mix(in srgb, ${primary} 22%, transparent)`,
  };

  if (!canViewSales) {
    return (
      <div className="alert alert-warning" role="alert">
        No tienes permiso para consultar los movimientos
        de ventas.
      </div>
    );
  }

  return (
    <div
      className="company-sales pos-movements"
      style={theme}
    >
      <section className="sales-header">
        <div>
          <p className="sales-kicker">Punto de venta</p>

          <h1>Últimos movimientos</h1>

          <p>
            Consulta tus ventas y sus comprobantes asociados.
          </p>
        </div>
      </section>

      <section
        className="recent-sales"
        aria-busy={loading}
      >
        <div className="recent-sales-heading">
          <div>
            <h2>Últimas ventas</h2>

            <p>
              Búsqueda sobre las últimas {SALES_LIMIT} ventas
              consultadas.
            </p>
          </div>

          <button
            type="button"
            disabled={loading || openingAction}
            onClick={() => {
              setPage(1);
              loadRecentSales();
            }}
          >
            {loading ? "Cargando…" : "↻ Actualizar"}
          </button>
        </div>

        <div className="movements-toolbar">
          <label className="movements-page-size">
            <span>Mostrar</span>

            <select
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>

            <span>filas</span>
          </label>

          <div className="movements-search">
            <label htmlFor="movements-search">
              Buscar movimientos
            </label>

            <div className="movements-search-control">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                aria-hidden="true"
              >
                <circle
                  cx="10.5"
                  cy="10.5"
                  r="6.5"
                />

                <path d="m16 16 5 5" />
              </svg>

              <input
                id="movements-search"
                type="search"
                placeholder="Venta, cliente, fecha, pago o estado…"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
              />
            </div>
          </div>
        </div>

        {error && (
          <div
            className="alert alert-danger"
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="recent-sales-table-wrap">
          <table className="recent-sales-table">
            <thead>
              <tr>
                <th scope="col">Venta</th>
                <th scope="col">Fecha</th>
                <th scope="col">Cliente</th>
                <th scope="col">Pago</th>
                <th scope="col">Estado</th>
                <th scope="col">Total</th>
                <th scope="col">Acciones</th>
              </tr>
            </thead>

            <tbody>
              {filteredSales.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    className="no-sales"
                  >
                    {loading
                      ? "Cargando movimientos…"
                      : error
                        ? "No se pudieron cargar los movimientos."
                        : search.trim()
                          ? "No se encontraron movimientos con esa búsqueda."
                          : "Aún no hay ventas registradas."}
                  </td>
                </tr>
              ) : (
                visibleSales.map((sale) => (
                  <tr key={sale.id}>
                    <td>
                      {sale.document_number || `#${sale.id}`}
                    </td>

                    <td>
                      {formatDate(sale.created_at)}
                    </td>

                    <td>
                      {sale.customer_name || "Cliente general"}
                    </td>

                    <td>
                      {getPaymentLabel(sale.payment_method)}
                    </td>

                    <td>
                      <span
                        className={`sale-status sale-status-${sale.status}`}
                      >
                        {getSaleStatusLabel(sale.status)}
                      </span>
                    </td>

                    <td>
                      <strong>
                        {money(sale.total)}
                      </strong>
                    </td>

                    <td className="sale-actions">
                      <button
                        type="button"
                        className="btn-sale-detail"
                        disabled={openingAction || loading}
                        onClick={() =>
                          openSaleModal(sale.id, "detail")
                        }
                      >
                        Ver
                      </button>

                      <button
                        type="button"
                        className="sale-action-btn btn-sale-reprint"
                        disabled={openingAction || loading}
                        onClick={() =>
                          handleReprintSale(sale.id)
                        }
                        title="Reimprimir comprobante con la marca COPIA"
                        aria-label={`Reimprimir copia de la venta ${
                          sale.document_number || `#${sale.id}`
                        }`}
                      >
                        {reprintingSaleId === sale.id
                          ? "Preparando…"
                          : "Reimprimir"}
                      </button>

                      {sale.status !== "cancelled" && (
                        <>
                          {canCancelSales && (
                            <>
                              <button
                                type="button"
                                className="btn-sale-return"
                                disabled={openingAction || loading}
                                onClick={() =>
                                  openSaleModal(
                                    sale.id,
                                    "return"
                                  )
                                }
                              >
                                Reintegrar
                              </button>

                              <button
                                type="button"
                                className="btn-sale-cancel"
                                disabled={openingAction || loading}
                                onClick={() =>
                                  openSaleModal(
                                    sale.id,
                                    "cancel"
                                  )
                                }
                              >
                                Anular
                              </button>
                            </>
                          )}

                          {canViewAccounting && (
                            <button
                              type="button"
                              className="sale-action-btn sale-action-accounting"
                              disabled={openingAction || loading}
                              onClick={() =>
                                handleViewJournalEntry(sale)
                              }
                            >
                              Asiento
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="movements-pagination">
          <p
            role="status"
            aria-live="polite"
          >
            Mostrando <strong>{firstRecord}</strong> a{" "}
            <strong>{lastRecord}</strong> de{" "}
            <strong>{filteredSales.length}</strong> movimientos
            {search.trim() &&
              ` (${recentSales.length} cargados)`}
          </p>

          <nav
            className="movements-pagination-buttons"
            aria-label="Paginación de movimientos"
          >
            <button
              type="button"
              disabled={currentPage === 1 || loading}
              onClick={() => setPage(1)}
              aria-label="Primera página"
            >
              «
            </button>

            <button
              type="button"
              disabled={currentPage === 1 || loading}
              onClick={() =>
                setPage(currentPage - 1)
              }
              aria-label="Página anterior"
            >
              ‹
            </button>

            <span className="movements-page-indicator">
              Página {currentPage} de {totalPages}
            </span>

            <button
              type="button"
              disabled={
                currentPage === totalPages || loading
              }
              onClick={() =>
                setPage(currentPage + 1)
              }
              aria-label="Página siguiente"
            >
              ›
            </button>

            <button
              type="button"
              disabled={
                currentPage === totalPages || loading
              }
              onClick={() => setPage(totalPages)}
              aria-label="Última página"
            >
              »
            </button>
          </nav>
        </div>
      </section>

      {saleModal &&
        canViewSales &&
        (saleModal.mode === "detail" || canCancelSales) && (
          <SaleReturnModal
            key={`${saleModal.sale.id}-${saleModal.mode}`}
            sale={saleModal.sale}
            mode={saleModal.mode}
            onClose={() => setSaleModal(null)}
            onCompleted={async (successMessage) => {
              setSaleModal(null);

              toast.success(
                successMessage || "Operación completada."
              );

              await loadRecentSales();
            }}
          />
        )}

      {linkedJournalEntry && canViewAccounting && (
        <div
          className="sale-linked-entry-backdrop"
          onClick={() => setLinkedJournalEntry(null)}
        >
          <section
            className="sale-linked-entry-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pos-journal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 id="pos-journal-title">
              Comprobante contable asociado
            </h3>

            <p>
              <strong>Número:</strong>{" "}
              {linkedJournalEntry.number || "-"}
            </p>

            <p>
              <strong>Fecha:</strong>{" "}
              {formatEntryDate(
                linkedJournalEntry.entry_date
              )}
            </p>

            <p>
              <strong>Descripción:</strong>{" "}
              {linkedJournalEntry.description || "-"}
            </p>

            <p>
              <strong>Estado:</strong>{" "}
              {linkedJournalEntry.status || "-"}
            </p>

            <div className="sale-linked-entry-actions">
              <button
                type="button"
                className="sale-action-btn"
                autoFocus
                onClick={() =>
                  setLinkedJournalEntry(null)
                }
              >
                Cerrar
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}